import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBuilding,
  faCalendarDay,
  faCircleInfo,
  faMoneyBillWave,
  faWallet
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../../Pages/dash.css';
import {
  formatDateTime,
  formatKes,
  formatShortDate,
  getDateTimeInputValue,
  getMonthKey,
  getMonthLabel
} from '../../utils/financeWorkflow';

const PAYMENT_METHODS = ['Bank Transfer', 'MPESA', 'Cash', 'Card', 'Cheque', 'Other'];

function ViewFixedBillComp() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const [bill, setBill] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [branchName, setBranchName] = useState('Campus not set');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [formState, setFormState] = useState({
    amount: '',
    paymentDate: getDateTimeInputValue(),
    paymentMethod: PAYMENT_METHODS[0],
    reference: '',
    notes: '',
    payee: ''
  });

  const loadBillPage = useCallback(async () => {
    if (!id) {
      setBill(null);
      setVendor(null);
      setPayments([]);
      setBranchName('Campus not set');
      return;
    }

    const billSnap = await getDoc(doc(db, 'Bills', id));
    if (!billSnap.exists()) {
      setBill(null);
      setVendor(null);
      setPayments([]);
      setBranchName('Campus not set');
      return;
    }

    const billData = { id: billSnap.id, ...billSnap.data() };
    setBill(billData);

    const paymentSnapshot = await getDocs(query(collection(db, 'BillPayments'), where('billId', '==', id)));
    const paymentItems = paymentSnapshot.docs
      .map((paymentDoc) => ({ id: paymentDoc.id, ...paymentDoc.data() }))
      .sort((first, second) => {
        const firstTime = first.paymentDate?.seconds || first.createdAt?.seconds || 0;
        const secondTime = second.paymentDate?.seconds || second.createdAt?.seconds || 0;
        return secondTime - firstTime;
      });
    setPayments(paymentItems);

    if (billData.vendor) {
      const vendorSnap = await getDoc(doc(db, 'Vendors', billData.vendor));
      if (vendorSnap.exists()) {
        const vendorData = { id: vendorSnap.id, ...vendorSnap.data() };
        setVendor(vendorData);
        setFormState((currentForm) => ({
          ...currentForm,
          payee: currentForm.payee || vendorData.vendorName || ''
        }));

        if (vendorData.branch && vendorData.branch !== 'General') {
          const branchSnap = await getDoc(doc(db, 'Branches', vendorData.branch));
          setBranchName(branchSnap.exists() ? branchSnap.data().branchName : 'Campus not set');
        } else {
          setBranchName(vendorData.branch === 'General' ? 'General' : 'Campus not set');
        }
      } else {
        setVendor(null);
        setBranchName('Campus not set');
      }
    } else {
      setVendor(null);
      setBranchName('Campus not set');
    }
  }, [id]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadBillPage();
      } catch (error) {
        console.error('Failed to load fixed bill:', error);
        swal('Error', 'This fixed bill could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [loadBillPage]);

  const expectedAmount = Number(bill?.billAmount || 0);
  const periodPayments = useMemo(() => {
    return payments.filter((payment) => {
      const paymentPeriodKey = payment.billingPeriodKey || getMonthKey(payment.paymentDate);
      return paymentPeriodKey === periodKey;
    });
  }, [payments, periodKey]);

  const periodPaid = periodPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const remainingAmount = Math.max(expectedAmount - periodPaid, 0);
  const allTimePaid = payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const paymentProgress = expectedAmount > 0 ? Math.min((periodPaid / expectedAmount) * 100, 100) : 0;
  const paymentTone = remainingAmount <= 0 && expectedAmount > 0 ? 'success' : periodPaid > 0 ? 'warning' : 'danger';
  const paymentStatus = expectedAmount <= 0 ? 'Amount not set' : remainingAmount <= 0 ? 'Settled' : periodPaid > 0 ? 'Partially paid' : 'Not paid';

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormState((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  };

  const handleSavePayment = async () => {
    if (!auth.currentUser) {
      swal('Error', 'You need to be signed in to record bill payments.', 'error');
      return;
    }

    if (!bill) {
      return;
    }

    const amountValue = Number(formState.amount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      swal('Invalid amount', 'Payment amount should be greater than zero.', 'warning');
      return;
    }

    if (amountValue > remainingAmount) {
      swal('Amount too high', `You can only pay up to ${formatKes(remainingAmount)} for ${getMonthLabel(periodKey)}.`, 'warning');
      return;
    }

    const paymentDateValue = new Date(formState.paymentDate);
    if (Number.isNaN(paymentDateValue.getTime())) {
      swal('Invalid date', 'Please choose a valid payment date.', 'warning');
      return;
    }

    setSubmitting(true);

    try {
      const paymentRef = doc(collection(db, 'BillPayments'));
      await setDoc(paymentRef, {
        billId: bill.id,
        billTitle: bill.billTitle || 'Fixed bill',
        billType: 'Fixed Bill',
        vendorId: vendor?.id || bill.vendor || '',
        vendorName: vendor?.vendorName || '',
        branchId: vendor?.branch || '',
        payee: formState.payee.trim() || vendor?.vendorName || bill.billTitle || 'Vendor',
        amount: amountValue,
        paymentDate: Timestamp.fromDate(paymentDateValue),
        paymentMethod: formState.paymentMethod,
        reference: formState.reference.trim(),
        notes: formState.notes.trim(),
        billingPeriodKey: periodKey,
        billingPeriodLabel: getMonthLabel(periodKey),
        expectedAmount,
        createdAt: Timestamp.now(),
        createdBy: auth.currentUser.uid,
        docId: paymentRef.id
      });

      swal('Success', 'Bill payment recorded successfully.', 'success');
      setFormState({
        amount: '',
        paymentDate: getDateTimeInputValue(),
        paymentMethod: PAYMENT_METHODS[0],
        reference: '',
        notes: '',
        payee: vendor?.vendorName || ''
      });
      await loadBillPage();
    } catch (error) {
      console.error('Failed to record fixed bill payment:', error);
      swal('Error', 'Bill payment could not be recorded right now.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="teamsComponent opsWorkspace">
        <div className="opsLoadingCard">
          <Spinner animation="border" role="status" />
          <span>Loading fixed bill...</span>
        </div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="teamsComponent opsWorkspace">
        <div className="opsEmptyState">
          <h6>Fixed bill not found</h6>
          <p>The bill you are looking for could not be found.</p>
          <button type="button" className="opsGhostBtn" onClick={() => navigate('/fixed-bills')}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back to fixed bills
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="teamsComponent opsWorkspace">
      <section className="opsHero">
        <div className="opsHeroCopy">
          <span className="opsEyebrow">Fixed bill</span>
          <h3>{bill.billTitle || 'Fixed bill'}</h3>
          <div className="opsHeroMeta">
            <span>{vendor?.vendorName || 'Vendor not set'}</span>
            <span>{branchName}</span>
            <span>{bill.billCycle || 'Monthly'} cycle</span>
            <span>Due on day {bill.billDueDate || '—'}</span>
          </div>
        </div>
        <div className="opsHeroActions">
          <button type="button" className="opsGhostBtn opsGhostBtn--hero" onClick={() => navigate('/fixed-bills')}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
        </div>
      </section>

      <section className="opsStatsGrid">
        <article className="opsStatCard">
          <span>{getMonthLabel(periodKey)} expected</span>
          <strong>{formatKes(expectedAmount)}</strong>
        </article>
        <article className="opsStatCard">
          <span>Paid this period</span>
          <strong>{formatKes(periodPaid)}</strong>
        </article>
        <article className="opsStatCard">
          <span>Remaining this period</span>
          <strong>{formatKes(remainingAmount)}</strong>
        </article>
        <article className="opsStatCard">
          <span>All-time paid</span>
          <strong>{formatKes(allTimePaid)}</strong>
        </article>
      </section>

      <section className="opsLayout">
        <article className="opsPanel opsPanel--wide">
          <div className="opsToolbar">
            <div>
              <h4>Payment history</h4>
              <p>Every payment recorded against this fixed bill, with the month it was applied to.</p>
            </div>
            <div className="opsFilters">
              <Form.Control type="month" value={periodKey} onChange={(event) => setPeriodKey(event.target.value)} />
            </div>
          </div>

          <div className="opsCoverageCard">
            <div>
              <span>Payment status</span>
              <strong>{paymentStatus}</strong>
            </div>
            <span className={`opsStatusPill opsStatusPill--${paymentTone}`}>{paymentStatus}</span>
          </div>
          <div className="employeeSalaryBar opsProgressBar">
            <span style={{ width: `${paymentProgress}%` }}></span>
          </div>

          <div className="appsTableCard opsHistoryCard">
            <div className="appsTableHeader">
              <h6>Bill payments</h6>
              <span>{payments.length} records</span>
            </div>
            <div className="appsTableWrap">
              <Table hover responsive className="appsTable">
                <thead>
                  <tr>
                    <th>Payment date</th>
                    <th>Applied month</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Reference</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id || payment.createdAt?.seconds}>
                      <td>{formatDateTime(payment.paymentDate)}</td>
                      <td>{payment.billingPeriodLabel || getMonthLabel(payment.billingPeriodKey || getMonthKey(payment.paymentDate))}</td>
                      <td>{formatKes(payment.amount)}</td>
                      <td>{payment.paymentMethod || '—'}</td>
                      <td>{payment.reference || '—'}</td>
                      <td>{payment.notes || '—'}</td>
                    </tr>
                  ))}
                  {payments.length === 0 && (
                    <tr>
                      <td colSpan="6" className="appsEmptyState">
                        No payments recorded for this fixed bill yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </article>

        <aside className="opsPanel">
          <div className="opsSectionHeader">
            <div className="opsSectionIcon">
              <FontAwesomeIcon icon={faWallet} />
            </div>
            <div>
              <h4>Record payment</h4>
              <p>Pay this bill directly from its page and keep the balance accurate for the selected month.</p>
            </div>
          </div>

          <div className="opsInfoGrid">
            <div className="opsInfoCard">
              <span><FontAwesomeIcon icon={faMoneyBillWave} /> Expected</span>
              <strong>{formatKes(expectedAmount)}</strong>
            </div>
            <div className="opsInfoCard">
              <span><FontAwesomeIcon icon={faCalendarDay} /> Applied month</span>
              <strong>{getMonthLabel(periodKey)}</strong>
            </div>
            <div className="opsInfoCard">
              <span><FontAwesomeIcon icon={faBuilding} /> Vendor</span>
              <strong>{vendor?.vendorName || 'Vendor not set'}</strong>
            </div>
            <div className="opsInfoCard">
              <span><FontAwesomeIcon icon={faCircleInfo} /> Remaining</span>
              <strong>{formatKes(remainingAmount)}</strong>
            </div>
          </div>

          <div className="opsComposer">
            <Form.Group className="mb-3">
              <Form.Label>Payment month</Form.Label>
              <Form.Control type="month" value={periodKey} onChange={(event) => setPeriodKey(event.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Payee</Form.Label>
              <Form.Control name="payee" value={formState.payee} onChange={handleFormChange} placeholder="Recipient name" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control type="number" name="amount" value={formState.amount} onChange={handleFormChange} placeholder="0" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Payment date</Form.Label>
              <Form.Control type="datetime-local" name="paymentDate" value={formState.paymentDate} onChange={handleFormChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Method</Form.Label>
              <Form.Select name="paymentMethod" value={formState.paymentMethod} onChange={handleFormChange}>
                {PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Reference</Form.Label>
              <Form.Control name="reference" value={formState.reference} onChange={handleFormChange} placeholder="Transaction reference" />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Notes</Form.Label>
              <Form.Control as="textarea" rows={3} name="notes" value={formState.notes} onChange={handleFormChange} placeholder="Optional note for finance" />
            </Form.Group>

            <p className="opsFormNote">
              You can only pay up to the remaining balance for {getMonthLabel(periodKey)}.
            </p>

            <Button variant="dark" className="w-100" onClick={handleSavePayment} disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" /> Saving...
                </>
              ) : (
                'Record payment'
              )}
            </Button>
          </div>

          <div className="opsMiniSummary">
            <h6>Vendor details</h6>
            <div className="opsMiniSummaryRow">
              <span>Phone</span>
              <strong>{vendor?.vendorPhone || 'Not set'}</strong>
            </div>
            <div className="opsMiniSummaryRow">
              <span>Email</span>
              <strong>{vendor?.vendorEmail || 'Not set'}</strong>
            </div>
            <div className="opsMiniSummaryRow">
              <span>Started</span>
              <strong>{formatShortDate(vendor?.commDate, 'Not set')}</strong>
            </div>
          </div>

          {vendor?.paymentDetails ? (
            <div className="opsInsightCard">
              <div className="opsInsightIcon">
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <div>
                <strong>Payment details</strong>
                <p>{vendor.paymentDetails}</p>
              </div>
            </div>
          ) : null}
        </aside>
      </section>
    </div>
  );
}

export default ViewFixedBillComp;
